import { useState } from "react";
import { ArrowDownUp, Check, EyeOff, Loader2, NotebookPen, Pencil, Save, Trash2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import CoachListSection from "./CoachListSection";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import LoadFailedCard from "./LoadFailedCard";
import { FailureCard, SavedCard } from "./GoalNotice";
import { EmptyList, ICON_BTN, LINK_BTN, ListLoading, NAVY_BTN, NOTES_FRAME, ROW_FRAME, SelectBox, TEXTAREA } from "./CoachNotesKit";
import { REFLECTION_SORTS, sortRows } from "../lib/coachListSort";

/**
 * Issue #205 — the coach's own observations, on a separate surface from
 * Feedback so there is no toggle to get wrong and no path by which one
 * becomes the other. No session picker (Martin dropped the training-session
 * link), no categories, no pinning: coach -> player -> body.
 *
 * Rendered for admin too — with an explanation instead of a list — rather
 * than hidden: admin being unable to read reflections is a decision worth
 * stating in the product, not a surface that silently isn't there. The API
 * denies admin outright (403); this is not a UI hide.
 *
 * Lifted out of `CoachPortal.jsx` for issue #460 (see `CoachGoalsPanel.jsx`
 * for why), gaining only the sort control.
 */
export default function CoachReflectionsPanel(props) {
  // #1221 slice 3: the coach's player page passes `appearance="r5"` and gets the
  // kit version below, called Private notes. Unset (the flag-off page) is the
  // shipped panel, words and all.
  if (props.appearance === "r5") return <PrivateNotesPanelR5 {...props} />;
  return <ShippedCoachReflectionsPanel {...props} />;
}

function ShippedCoachReflectionsPanel({
  reflections,
  sort,
  onSortChange,
  isCoach,
  firstName,
  editing,
  onEditingChange,
  onBeginEdit,
  onSaveEdit,
  onDelete,
  body,
  onBodyChange,
  onAdd,
}) {
  const rows = sortRows(REFLECTION_SORTS, sort, reflections);

  /* COPY IS LOAD-BEARING — do not "improve" this to "private" or "only you
     will ever see this". Under the NZ Privacy Act IPP6 a reflection naming
     a player is still that player's personal information, which they can
     request, and an operator with database access can read the row. "Not
     shown to the player" is a promise this codebase actually keeps; an
     absolute one is not on offer. */
  const privacyCopy = (
    <p className="text-sm text-slate-500 mt-1" data-testid="cp-reflections-privacy-copy">
      Your own observations about {firstName} — <strong className="text-slate-700">not shown to the player</strong>, and not shown to their other coaches. To write something the player should read, use the Feedback tab.
    </p>
  );

  if (!isCoach) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg text-slate-900">My reflections</h3>
          {privacyCopy}
          <p className="text-sm text-slate-500 mt-4" data-testid="cp-reflections-not-for-you">
            Reflections belong to the coach who wrote them. They are not visible to administrators.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6">
        <CoachListSection
          title="My reflections"
          sorts={REFLECTION_SORTS}
          sortValue={sort}
          onSortChange={onSortChange}
          sortTestId="cp-reflections-sort"
          listTestId="portal-reflections"
          description={privacyCopy}
        >
          {rows.length === 0 && (
            <p className="text-sm text-slate-500">No reflections on {firstName} yet.</p>
          )}
          {rows.map((r) => {
            const isEditing = editing?.reflection_id === r.reflection_id;
            return (
              <div key={r.reflection_id} className="p-3 rounded-lg border border-slate-200 group relative" data-testid={`cp-reflection-${r.reflection_id}`}>
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      rows={3}
                      value={editing.body}
                      onChange={(e) => onEditingChange({ ...editing, body: e.target.value })}
                      data-testid={`cp-reflection-edit-input-${r.reflection_id}`}
                    />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => onEditingChange(null)}>Cancel</Button>
                      <Button size="sm" onClick={onSaveEdit} style={{ background: "#0B2E59" }} data-testid={`cp-reflection-save-${r.reflection_id}`}>
                        <Save size={13} className="mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm text-slate-800 whitespace-pre-wrap flex-1 min-w-0">{r.body}</div>
                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                        <button onClick={() => onBeginEdit(r)} title="Edit" className="text-slate-500 hover:text-slate-900" data-testid={`cp-reflection-edit-${r.reflection_id}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => onDelete(r)} title="Delete" className="text-slate-500 hover:text-red-600" data-testid={`cp-reflection-del-${r.reflection_id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1.5">
                      {r.created_at?.slice(0, 10)}{r.updated_at ? " · edited" : ""}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </CoachListSection>

        <div className="mt-4 border-t border-slate-100 pt-4 space-y-2">
          <Textarea
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            placeholder="What you noticed, for your own record…"
            rows={3}
            data-testid="cp-reflection-input"
          />
          <Button onClick={onAdd} className="w-full" style={{ background: "#0B2E59" }} data-testid="cp-reflection-add">
            <EyeOff size={14} className="mr-2" /> Save reflection
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// #1221 slice 3 (D240, D251, D256) — `CoachPlayerFeedback.dc.html`'s second half: Private notes.
//
// TRUST PATH. This is the coach's own record on a player and it must never reach
// the player or another coach. This slice adds no read and no write: the same
// `/coach-reflections` calls, the same shapes, as before. What changed is the
// frame and the words.
//
// The line saying who can read a note is in every state (loading, empty,
// failed, filled and the admin's), above the list and before the box: a coach reads
// who can see a note before writing one. The words are D256's, not the board's
// "Only you see these", which would break the IPP6 honesty rule the shipped copy
// above keeps and journeys 07 and 12 assert: under the NZ Privacy Act a note naming
// a player is still that player's personal information, and an operator with
// database access can read the row. "Not shown to" is a promise this codebase
// keeps; "only you" is not on offer. The board's second sentence (to write
// something the player should read, use Feedback) sits in the same callout.
//
// The half is called "Private notes" on screen everywhere; "My reflections" and
// "Reflections" belong to the player's own section now. As the board draws it
// (D337, D259) the filled, empty and admin states open with the "Private notes"
// heading, then Newest first when there are notes, then the privacy line; the
// loading and failed states open with the line itself.
// ---------------------------------------------------------------------------

function PrivacyLine({ firstName }) {
  // The board's callout (D259): one tinted box, the eye-off glyph, one paragraph
  // holding D256's sentence and the pointer to Feedback.
  return (
    <div
      data-testid="cp-reflections-privacy-copy"
      style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", backgroundColor: "var(--nw-navy-tint)", borderRadius: 10 }}
    >
      <EyeOff size={16} color="#B42318" strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>
        {`Your notes on ${firstName} — not shown to ${firstName} or their other coaches. To write something ${firstName} should read, use Feedback to ${firstName}.`}
      </p>
    </div>
  );
}

function NotesHeading({ size }) {
  return (
    <h3 tabIndex={-1} data-focus-after-delete style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: size, color: "var(--nw-ink)", outline: "none" }}>
      Private notes
    </h3>
  );
}

function PrivateNoteRow({ r, editing, onEditingChange, onBeginEdit, onSaveEdit, onRequestDelete }) {
  const id = r.reflection_id;
  const isEditing = editing?.reflection_id === id;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const empty = !editing?.body?.trim();
  const save = async () => {
    // An empty note is refused by the API for good (400), so it is never sent.
    if (saving || empty) return;
    setSaving(true);
    setSaveError(false);
    try {
      await onSaveEdit();
    } catch {
      setSaveError(true);
    }
    setSaving(false);
  };

  if (isEditing) {
    return (
      <div data-testid={`cp-reflection-${id}`} style={ROW_FRAME}>
        <textarea
          rows={3}
          value={editing.body}
          disabled={saving}
          aria-label="Edit your private note"
          onChange={(e) => onEditingChange({ ...editing, body: e.target.value })}
          data-testid={`cp-reflection-edit-input-${id}`}
          style={TEXTAREA}
        />
        {saveError && <FailureCard data-testid="cp-reflection-edit-error" title="Failed to save note" body="Your note is still in the box. Try again." />}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16 }}>
          <button type="button" onClick={() => onEditingChange(null)} disabled={saving} data-testid={`cp-reflection-cancel-${id}`} style={LINK_BTN}>
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving || empty} data-testid={`cp-reflection-save-${id}`} style={{ ...NAVY_BTN, opacity: saving || empty ? 0.7 : 1, cursor: saving || empty ? "not-allowed" : "pointer" }}>
            {saving ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Save size={16} aria-hidden="true" />}
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`cp-reflection-${id}`} style={ROW_FRAME}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
        <div style={{ flexGrow: 1, minWidth: 0, fontSize: 15, lineHeight: 1.5, color: "var(--nw-ink)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{r.body}</div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button type="button" aria-label="Edit note" onClick={() => onBeginEdit(r)} data-testid={`cp-reflection-edit-${id}`} style={ICON_BTN}>
            <Pencil size={16} color="var(--nw-navy)" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Delete note" onClick={() => onRequestDelete(r)} data-testid={`cp-reflection-del-${id}`} style={ICON_BTN}>
            <Trash2 size={16} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>
        {r.created_at?.slice(0, 10)}{r.updated_at ? " · edited" : ""}
      </div>
    </div>
  );
}

function PrivateNotesPanelR5({
  status = "loaded",
  isCoach,
  firstName,
  reflections,
  sort,
  onSortChange,
  editing,
  onEditingChange,
  onBeginEdit,
  onSaveEdit,
  onRemove,
  body,
  onBodyChange,
  onAdd,
  onRetry,
}) {
  const rows = sortRows(REFLECTION_SORTS, sort, reflections);
  const [adding, setAdding] = useState(false);
  const [saved, setSaved] = useState(false);
  const [addError, setAddError] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const add = async () => {
    if (adding || !body.trim()) return;
    setAdding(true);
    setSaved(false);
    setAddError(false);
    try {
      await onAdd();
      setSaved(true);
    } catch {
      setAddError(true);
    }
    setAdding(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onRemove(deleteTarget);
      setDeleteTarget(null);
    } catch {
      setDeleteError("Nothing has been lost — we just couldn't reach the server.");
    }
    setDeleting(false);
  };

  const privacy = <PrivacyLine firstName={firstName} />;

  if (!isCoach) {
    return (
      <div style={NOTES_FRAME}>
        <NotesHeading size={15} />
        {privacy}
        <p data-testid="cp-reflections-not-for-you" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>
          Private notes belong to the coach who wrote them. Administrators can't see them.
        </p>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div style={NOTES_FRAME}>
        {privacy}
        <ListLoading testId="cp-reflections-loading" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div style={NOTES_FRAME}>
        {privacy}
        <LoadFailedCard title="Couldn't load your private notes on this player" onRetry={onRetry} testId="cp-reflections-load-failed" />
      </div>
    );
  }

  return (
    <div style={NOTES_FRAME}>
      <NotesHeading size={rows.length === 0 ? 15 : 17} />
      {rows.length > 0 && (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <SelectBox
          icon={ArrowDownUp}
          value={sort}
          onChange={onSortChange}
          ariaLabel="Sort private notes"
          testId="cp-reflections-sort"
          options={REFLECTION_SORTS.map((s) => ({ value: s.value, label: s.label }))}
        />
      </div>
      )}
      {privacy}

      {rows.length === 0 ? (
        <EmptyList testId="portal-reflections" icon={NotebookPen} title={`No private notes on ${firstName} yet.`} />
      ) : (
        <div data-testid="portal-reflections" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((r) => (
            <PrivateNoteRow
              key={r.reflection_id}
              r={r}
              editing={editing}
              onEditingChange={onEditingChange}
              onBeginEdit={onBeginEdit}
              onSaveEdit={onSaveEdit}
              onRequestDelete={(note) => {
                setDeleteError(null);
                setDeleteTarget(note);
              }}
            />
          ))}
        </div>
      )}

      <textarea
        value={body}
        onChange={(e) => {
          setSaved(false);
          onBodyChange(e.target.value);
        }}
        placeholder="What you noticed, for your own record…"
        aria-label="Add a private note"
        rows={3}
        data-testid="cp-reflection-input"
        style={TEXTAREA}
      />
      {addError && <FailureCard data-testid="cp-reflection-add-error" title="Failed to save note" body="Your note is still in the box. Try again." />}
      {saved && !addError && <SavedCard data-testid="cp-reflection-saved">{`Note saved — not shown to ${firstName}`}</SavedCard>}
      <button type="button" onClick={add} disabled={adding} data-testid="cp-reflection-add" style={{ ...NAVY_BTN, width: "100%", opacity: adding ? 0.7 : 1, cursor: adding ? "not-allowed" : "pointer" }}>
        {adding ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
        Save note
      </button>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        title="Delete this note?"
        confirmLabel="Delete note"
        testId="cp-reflection-delete"
        deleting={deleting}
        error={deleteError}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
